import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import type { ArticleCategory, DiscountType } from "@/types/camping";

const EMPTY_FORM = {
  name: "",
  discount_type: "percent_total" as DiscountType,
  value: "",
  article_category_id: "",
  valid_from: "",
  valid_until: "",
};

export interface CreateDiscountModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateDiscountModal({ open, onClose, onCreated }: CreateDiscountModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setSubmitting(false);
    api
      .get<ArticleCategory[]>("/api/v1/article-categories")
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        discount_type: form.discount_type,
        value: form.value.trim(),
      };
      if (form.discount_type === "percent_category") {
        body.article_category_id = form.article_category_id;
      }
      if (form.valid_from) body.valid_from = form.valid_from;
      if (form.valid_until) body.valid_until = form.valid_until;
      await api.post("/api/v1/discounts", body);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Neuer Rabatt" description="Stammdaten-Rabatt anlegen">
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Name
          <input
            required
            autoFocus
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Rabattart
          <select
            className="eh-input"
            value={form.discount_type}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                discount_type: e.target.value as DiscountType,
              }))
            }
          >
            <option value="percent_total">Prozent auf Gesamt</option>
            <option value="percent_category">Prozent auf Artikelkategorie</option>
            <option value="fixed">Fixbetrag (EUR)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Wert
          <input
            required
            type="number"
            min="0"
            step="0.01"
            max={form.discount_type === "fixed" ? undefined : "100"}
            className="eh-input w-40"
            value={form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
          />
        </label>
        {form.discount_type === "percent_category" ? (
          <label className="flex flex-col gap-1 text-sm">
            Artikelkategorie
            <select
              required
              className="eh-input"
              value={form.article_category_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, article_category_id: e.target.value }))
              }
            >
              <option value="">Bitte wählen…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            Gültig ab
            <input
              type="date"
              className="eh-input"
              value={form.valid_from}
              onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Gültig bis
            <input
              type="date"
              className="eh-input"
              value={form.valid_until}
              onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
            />
          </label>
        </div>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
